import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { apiCall } from '../services/apiUtil';

const POLL_INTERVAL_MS = 10000;

const PollContext = createContext(null);

export function PollProvider({ children, isAuthenticated }) {
  const [liveEvents, setLiveEvents] = useState([]);
  const [notifications, setNotifications] = useState({ unread: 0, items: [] });
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [games, setGames] = useState({});

  // Set of active game IDs to include in each poll
  const gameIdsRef = useRef(new Set());
  const intervalRef = useRef(null);

  // Debug helper — inspect full poll context from browser console: __poll()
  window.__poll = () => ({
    gameIds: Array.from(gameIdsRef.current),
    games,
    notifications,
    unreadMessages,
    liveEvents,
  });

  const fetchPoll = useCallback(async () => {
    if (document.visibilityState === 'hidden') return;
    const pollContext = {
      gameIds: Array.from(gameIdsRef.current),
    };
    try {
      const data = await apiCall('/poll', {
        method: 'POST',
        body: JSON.stringify(pollContext),
        silent: true,
      });
      setLiveEvents(data.liveEvents || []);
      setNotifications(data.notifications || { unread: 0, items: [] });
      setUnreadMessages(data.unreadMessages || 0);
      if (data.games) {
        setGames(prev => ({ ...prev, ...data.games }));
      }
    } catch (err) {
      // Silently ignore — apiUtil tracks consecutive failures globally
    }
  }, []);

  // Start/stop interval based on auth state
  useEffect(() => {
    if (!isAuthenticated) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      return;
    }
    fetchPoll(); // immediate fetch on login
    intervalRef.current = setInterval(fetchPoll, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [isAuthenticated, fetchPoll]);

  // On iOS, backgrounding the app kills in-flight fetches. Re-poll immediately
  // when the user returns so data is fresh without waiting for the next interval tick.
  useEffect(() => {
    if (!isAuthenticated) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchPoll();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, fetchPoll]);

  const registerGame = useCallback((gameId) => {
    if (!gameId) return;
    gameIdsRef.current.add(String(gameId));
  }, []);

  const unregisterGame = useCallback((gameId) => {
    if (!gameId) return;
    gameIdsRef.current.delete(String(gameId));
    setGames(prev => {
      const next = { ...prev };
      delete next[String(gameId)];
      return next;
    });
  }, []);

  // Optimistic merge into a polled game so callers don't have to wait for the
  // next 10s poll tick (used by pause/resume to flip status immediately).
  const patchGame = useCallback((gameId, partial) => {
    if (!gameId) return;
    const key = String(gameId);
    setGames(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), ...partial }
    }));
  }, []);

  // Optimistic: immediately zero unread count so the bell clears without waiting for next poll.
  const markAllNotificationsRead = useCallback(() => {
    setNotifications(prev => ({
      ...prev,
      unread: 0,
      items: prev.items.map(n => ({ ...n, status: 'read' })),
    }));
  }, []);

  // Optimistic: mark a single notification as read in poll state.
  const markNotificationRead = useCallback((notificationId) => {
    setNotifications(prev => ({
      ...prev,
      unread: Math.max(0, prev.unread - 1),
      items: prev.items.map(n => n.id === notificationId ? { ...n, status: 'read' } : n),
    }));
  }, []);

  return (
    <PollContext.Provider value={{ liveEvents, notifications, unreadMessages, games, registerGame, unregisterGame, patchGame, markAllNotificationsRead, markNotificationRead }}>
      {children}
    </PollContext.Provider>
  );
}

export function usePoll() {
  const ctx = useContext(PollContext);
  if (!ctx) throw new Error('usePoll must be used within a PollProvider');
  return ctx;
}
